import axios from "axios";
import { isFinalRequestError, isTimeoutError } from "@/lib/requestRetry";

/**
 * Why an Accept / Decline / Undo on an AI prediction failed, split the way the
 * reader has to react (ENG-186):
 *
 * - "A": refused in the browser before any request was sent. Nothing is locked.
 * - "B-rejected": the server answered 4xx (not 408/429). The decisions were reloaded,
 *   so a retry works.
 * - "B-unconfirmed": 5xx, 408, 429 or no response, after the automatic retries ran
 *   out (ENG-229). The decision stays unconfirmed and locks every verdict until the
 *   reader presses "Retry decision".
 */
export type VerdictFailureKind = "A" | "B-rejected" | "B-unconfirmed" | "unknown";

export type VerdictNotSentCode =
  | "decisions-not-ready"
  | "decision-busy"
  | "editor-not-ready"
  | "volume-not-ready"
  | "volume-missing"
  | "series-mismatch"
  | "segment-missing"
  | "no-schema"
  | "confidence-missing";

/** Thrown for case A so the toast can name the reason without parsing text. */
export class VerdictNotSentError extends Error {
  constructor(
    readonly code: VerdictNotSentCode,
    message: string,
  ) {
    super(message);
    this.name = "VerdictNotSentError";
  }
}

export interface VerdictFailure {
  kind: VerdictFailureKind;
  /** Short code shown to the reader, e.g. "A-volume-not-ready", "B-409", "B-network". */
  code: string;
  status?: number;
  /** Backend `detail` or the thrown message; console only, never in the toast. */
  detail?: string;
}

function backendDetail(data: unknown): string | undefined {
  const detail = (data as { detail?: unknown } | undefined)?.detail;
  if (detail == null) return undefined;
  return typeof detail === "string" ? detail : JSON.stringify(detail);
}

export function describeVerdictFailure(error: unknown): VerdictFailure {
  if (error instanceof VerdictNotSentError) {
    return { kind: "A", code: `A-${error.code}`, detail: error.message };
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status == null) {
      const code = isTimeoutError(error) ? "B-timeout" : "B-network";
      return { kind: "B-unconfirmed", code, detail: error.message };
    }
    return {
      // The same rule that decides retries: which failures reload vs. lock.
      kind: isFinalRequestError(error) ? "B-rejected" : "B-unconfirmed",
      code: `B-${status}`,
      status,
      detail: backendDetail(error.response?.data) ?? error.message,
    };
  }
  return {
    kind: "unknown",
    code: "unknown",
    detail: error instanceof Error ? error.message : String(error),
  };
}

const NOT_SENT_SUMMARY: Record<VerdictNotSentCode, string> = {
  "decisions-not-ready": "saved decisions are still loading. Wait a moment and try again.",
  "decision-busy": "another decision is still saving. Wait a moment and try again.",
  "editor-not-ready": "the editor is not ready. Close it and try again.",
  "volume-not-ready": "the images are still loading. Wait a moment and try again.",
  "volume-missing": "the images are not loaded. Reload the case and try again.",
  "series-mismatch":
    "the prediction belongs to another series than the one being annotated. Please report this code.",
  "segment-missing": "the prediction is missing. Please report this code.",
  "no-schema": "this finding type cannot be saved. Please report this code.",
  "confidence-missing": "enter your confidence (1–100) first.",
};

function rejectedSummary(status: number): string {
  if (status === 401) return "your sign-in expired. Sign in again.";
  if (status === 403) return "you do not have permission for this case.";
  if (status === 409) {
    return "this stone changed (maybe in another tab). The latest state is now shown; check it and try again.";
  }
  if (status === 423) return "this project is paused.";
  return "the server refused it. It was reloaded, so please try again.";
}

export type VerdictAction = "accept" | "decline" | "undo";

/** Short reader-facing toast: what happened, what to do, and the code to report. */
export function verdictFailureToast(action: VerdictAction, failure: VerdictFailure): string {
  const verb = action === "undo" ? "undo the decision" : action;
  if (failure.kind === "B-unconfirmed") {
    const done = action === "accept" ? "accepted" : action === "decline" ? "declined" : "undone";
    return `Decision "${done}" was not delivered. Use Retry decision above the cards to finish it. [${failure.code}]`;
  }
  let summary = "please try again.";
  if (failure.kind === "A") {
    summary = NOT_SENT_SUMMARY[failure.code.slice(2) as VerdictNotSentCode] ?? summary;
  } else if (failure.kind === "B-rejected" && failure.status != null) {
    summary = rejectedSummary(failure.status);
  }
  return `Could not ${verb}: ${summary} [${failure.code}]`;
}
